import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

interface VisualizeWidgetData {
  title: string;
  loading_messages?: string[];
  widget_code: string;
}

interface Props {
  data: VisualizeWidgetData;
}

export default function VisualizeWidget({ data }: Props) {
  const [loadingMsg, setLoadingMsg] = useState<string | null>(null);
  // If widget_code is already present (loaded from history), show immediately.
  // The loading animation is only for first-time generation UX.
  const [ready, setReady] = useState(Boolean(data.widget_code));

  useEffect(() => {
    // Already ready — skip animation
    if (data.widget_code) {
      setReady(true);
      return;
    }
    if (data.loading_messages && data.loading_messages.length > 0) {
      let idx = 0;
      setLoadingMsg(data.loading_messages[0]);
      const interval = setInterval(() => {
        idx = (idx + 1) % data.loading_messages!.length;
        setLoadingMsg(data.loading_messages![idx]);
      }, 1500);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setLoadingMsg(null);
        setReady(true);
      }, data.loading_messages.length * 1500 + 500);
      return () => { clearInterval(interval); clearTimeout(timeout); };
    } else {
      setReady(true);
    }
  }, [data.loading_messages, data.widget_code]);

  const code = data.widget_code ?? '';
  const isSvg = code.trim().startsWith("<svg");

  return (
    <div className="tool-visualize">
      {!ready && loadingMsg && (
        <div className="visualize-loading">
          <Loader2 size={16} className="spin" />
          <span>{loadingMsg}</span>
        </div>
      )}
      {ready && code && (
        <div className="visualize-content">
          {isSvg ? (
            <div
              className="visualize-svg"
              dangerouslySetInnerHTML={{ __html: code }}
            />
          ) : (
            <iframe
              className="visualize-iframe"
              srcDoc={code}
              sandbox="allow-scripts"
              title={data.title}
            />
          )}
        </div>
      )}
    </div>
  );
}
